import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { useGooglePlacesAutocomplete } from '~/hooks/useGooglePlacesAutocomplete';
import { useCoordinatorAccess } from '~/hooks/useIsCoordinator';
import {
  type ApiStudentType,
  radiusToMiles,
  rangeToTargetType,
  STUDENT_TYPE_LABEL_KEYS,
  toApiLanguage,
  toLanguageCode,
} from '~/modules/web-ads-targets/targetFields';
import { FieldError, FieldTitle } from '~/styles/forms/fieldFeedback';
import { FormField, StyledInput } from '~/styles/forms/formElements';
import { SearchSelect } from '~/styles/forms/SearchSelect';
import { SelectGroup } from '~/styles/forms/styledSelectGroup';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';
import type {
  LanguageCode,
  StudentType,
  TargetType,
} from '~/types/congregationGroupTypes';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/web-ads-targets/add',
)({
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search.id === 'number' ? search.id : undefined,
  }),
  component: RouteComponent,
});

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Legacy's radius options, which it reads from a site option that holds the
// same list in every language.
const RADIUS_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 50, 75, 100] as const;

// Endonym plus English name, as on the legacy site.
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English (English)' },
  { value: 'es', label: 'Español (Spanish)' },
  { value: 'fr', label: 'Français (French)' },
  { value: 'pt-br', label: 'Português (Portuguese)' },
] as const;

/**
 * A place chosen from the Places autocomplete, in the shape the API stores.
 *
 * Each region component is a `[long_name, short_name]` pair, as legacy stores
 * it. The matcher compares country on element 1 and the title formatter reads
 * element 0, so a one-element array would break country matching. A locality
 * has no distinct short name, so the long name appears twice.
 */
type PickedPlace = {
  lat: number;
  lng: number;
  /** The Places text — what the card's heading is derived from server-side. */
  formattedAddress: string;
  locality: [string, string] | null;
  administrativeAreaLevel1: [string, string] | null;
  country: [string, string] | null;
};

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/web-ads-targets/add',
  });
  const { id } = Route.useSearch();
  const isEditing = id !== undefined;

  const { isCoordinator, isResolved } = useCoordinatorAccess();
  const refused = isResolved && !isCoordinator;

  // Resolve the numeric group id (endpoints are keyed by id; the route only
  // carries the slug).
  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  // The edit form prefills from the same list the overview cards render, so it
  // opens on exactly the row the card showed.
  const { data: groupTargets } = api.listGroupGeoTargets.useQuery(
    { path: { group_id: groupId ?? 0 } },
    { enabled: !!groupId && isEditing },
  );
  const existingTarget = isEditing
    ? groupTargets?.find((target) => target.id === id)
    : undefined;

  const { mutateAsync: createGeoTarget, isPending: isCreating } =
    api.createGeoTarget.useMutation();
  const { mutateAsync: updateGeoTarget, isPending: isUpdating } =
    api.updateGeoTarget.useMutation();

  const [city, setCity] = useState('');
  const [cityError, setCityError] = useState(false);
  const [selectedCityDisplay, setSelectedCityDisplay] = useState('');
  const [place, setPlace] = useState<PickedPlace | null>(null);
  // Whether this visit chose a place. An edit that never touches the location
  // must leave the four region arrays out of the PATCH: absent means unchanged,
  // and re-sending them would count as a region change and re-derive the stored
  // heading.
  const [placePicked, setPlacePicked] = useState(false);
  const [languages, setLanguages] = useState<LanguageCode[]>([]);
  const [studentType, setStudentType] = useState<StudentType>('all');
  const [targetType, setTargetType] = useState<TargetType>('in-radius');
  const [radius, setRadius] = useState('5');
  const [seededFrom, setSeededFrom] = useState<number | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [radiusError, setRadiusError] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Seed the controls once, when the target arrives. `existingTarget` is
  // undefined on the first paint of an edit — the list query is still in flight
  // — so this cannot be `useState` initialisers.
  useEffect(() => {
    if (!existingTarget || seededFrom === existingTarget.id) return;
    const heading = existingTarget.address ?? existingTarget.title;
    setCity(heading);
    setSelectedCityDisplay(existingTarget.title);
    setPlace({
      lat: existingTarget.lat,
      lng: existingTarget.lng,
      formattedAddress: heading,
      locality: null,
      administrativeAreaLevel1: null,
      country: null,
    });
    setPlacePicked(false);
    setLanguages(existingTarget.languages.map(toLanguageCode));
    setStudentType(existingTarget.student_type.toLowerCase() as StudentType);
    setTargetType(rangeToTargetType(existingTarget.range));
    // `?? 5` matches legacy: a radius target with no stored radius falls to the
    // first option there, so an edit writes 5 miles. Whole-region targets have
    // no radius, which is why `isWholeRegionForm` omits `radius_miles`.
    setRadius(String(radiusToMiles(existingTarget.radius) ?? 5));
    setSeededFrom(existingTarget.id);
  }, [existingTarget, seededFrom]);

  useEffect(() => {
    if (refused) {
      navigate({
        to: '/groups/$groupName/',
        params: { groupName },
        replace: true,
      });
    }
  }, [refused, groupName, navigate]);

  // A `?id=` the group's list doesn't hold (deleted since, or another group's
  // target) goes back to the list rather than leaving the blank form up
  // forever.
  const missingTarget =
    isEditing && groupTargets !== undefined && existingTarget === undefined;
  useEffect(() => {
    if (missingTarget) {
      navigate({
        to: '/groups/$groupName/web-ads-targets/',
        params: { groupName },
        replace: true,
      });
    }
  }, [missingTarget, groupName, navigate]);

  const { inputRef } = useGooglePlacesAutocomplete({
    apiKey: GOOGLE_MAPS_API_KEY,
    onPlaceSelect: (address) => {
      const abbreviatedCountry =
        // "USA" rather than "US", as on the legacy site.
        address.countryCode === 'US'
          ? 'USA'
          : address.countryCode || address.country;
      const countryLong = address.countryName || address.country;
      const countryShort = address.countryCode || address.country;

      setCity(
        [address.city, address.stateCode || address.state, abbreviatedCountry]
          .filter(Boolean)
          .join(', '),
      );
      setSelectedCityDisplay(
        [address.city, address.state, countryLong].filter(Boolean).join(', '),
      );
      setPlace({
        lat: address.lat,
        lng: address.lng,
        formattedAddress: address.formattedAddress || '',
        locality: address.city ? [address.city, address.city] : null,
        administrativeAreaLevel1: address.state
          ? [address.state, address.stateCode || address.state]
          : null,
        country: countryLong ? [countryLong, countryShort] : null,
      });
      setPlacePicked(true);
    },
  });

  const backToList = () =>
    navigate({
      to: '/groups/$groupName/web-ads-targets/',
      params: { groupName },
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLimitReached(false);
    setRadiusError(false);
    setSubmitError(null);

    // A place must be picked from the autocomplete so we have coordinates. On an
    // edit the coordinates are already seeded from the stored target.
    if (!city.trim() || !place || !groupId) {
      setCityError(true);
      return;
    }

    const isWholeRegionForm = targetType === 'whole-region';
    // The form has no equivalent of legacy's whole-region sub-type choice,
    // which only support staff saw, so LOCALITY is the only whole-region range
    // it can produce.
    const range = isWholeRegionForm ? 'LOCALITY' : 'RADIUS';
    // `formattedAddress` is only set by a pick. An edit that keeps the stored
    // location re-posts it verbatim, as legacy does, which leaves the stored
    // heading alone.
    const address = (placePicked ? place.formattedAddress : '') || city.trim();
    const apiLanguages = languages.map(toApiLanguage);
    const apiStudentType = studentType.toUpperCase() as ApiStudentType;

    try {
      if (existingTarget) {
        await updateGeoTarget({
          path: { group_id: groupId, target_id: existingTarget.id },
          body: {
            address,
            lat: place.lat,
            lng: place.lng,
            languages: apiLanguages,
            student_type: apiStudentType,
            // `range` only when the user changed the target type. Sending it
            // always would rewrite area-level targets to LOCALITY on any edit,
            // since this control can't express them; legacy doesn't post the
            // field for these users either, so the stored value survives.
            ...(rangeToTargetType(existingTarget.range) !== targetType
              ? { range }
              : {}),
            ...(isWholeRegionForm ? {} : { radius_miles: Number(radius) }),
            ...(placePicked && isWholeRegionForm
              ? {
                  locality: place.locality,
                  administrative_area_level_1: place.administrativeAreaLevel1,
                  country: place.country,
                }
              : {}),
          },
        });
      } else {
        await createGeoTarget({
          path: { group_id: groupId },
          body: {
            address,
            lat: place.lat,
            lng: place.lng,
            range,
            radius_miles: isWholeRegionForm ? null : Number(radius),
            languages: apiLanguages,
            student_type: apiStudentType,
            // `administrative_area_level_2` is absent by design: the Places
            // hook extracts no county, and LOCALITY, the only whole-region
            // range this form produces, reads locality, level 1 and country.
            locality: isWholeRegionForm ? place.locality : null,
            administrative_area_level_1: isWholeRegionForm
              ? place.administrativeAreaLevel1
              : null,
            country: isWholeRegionForm ? place.country : null,
          },
        });
      }
    } catch (error) {
      if (isErrorCode(error, 'web_target_limit_reached')) {
        setLimitReached(true);
      } else if (isErrorCode(error, 'radius_required')) {
        setRadiusError(true);
      } else {
        setSubmitError(t('wbs.something-went-wrong-please-2'));
      }
      return;
    }

    await api.listGroupGeoTargets.invalidateQueries();
    await api.listGeoTargetMap.invalidateQueries();
    backToList();
  };

  const showRadiusField = targetType === 'in-radius';

  if (refused) return null;
  // An edit cannot render its controls before the row it is editing has landed:
  // an empty form here would submit blanks over a live target.
  if (isEditing && !existingTarget) return null;

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('wbs.web-targets')}
          placement="web_ads_targets"
        />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={backToList} />
        {limitReached && (
          <RefusalBox data-testid="web-target-limit-refusal">
            <AlertBox variant="error">
              <RefusalTitle>{t('wbs.web-target-limit-title')}</RefusalTitle>
              {/* Both sentences: the second is the only instruction the
                 refusal gives, and deleting a target releases the students
                 it matched. */}
              <RefusalMessage>
                {t('wbs.web-target-limit-message')}
              </RefusalMessage>
            </AlertBox>
          </RefusalBox>
        )}
        {submitError && (
          <RefusalBox>
            <AlertBox variant="error">{submitError}</AlertBox>
          </RefusalBox>
        )}
        <form onSubmit={handleSubmit}>
          <FormField>
            <FieldTitle>{t('wbs.languages')}</FieldTitle>
            <SearchSelect
              options={LANGUAGE_OPTIONS}
              value={languages}
              onChange={(v) => setLanguages(v as LanguageCode[])}
            />
          </FormField>

          <FormField>
            {/* Translation Needed */}
            <FieldTitle htmlFor="studentType">Student Type</FieldTitle>
            <SelectGroup>
              <select
                id="studentType"
                value={studentType}
                style={{ color: 'var(--grey-900)' }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === 'all' ||
                    value === 'postal' ||
                    value === 'online'
                  ) {
                    setStudentType(value);
                  }
                }}
              >
                <option value="all">{t(STUDENT_TYPE_LABEL_KEYS.ALL)}</option>
                <option value="postal">
                  {t(STUDENT_TYPE_LABEL_KEYS.POSTAL)}
                </option>
                <option value="online">
                  {t(STUDENT_TYPE_LABEL_KEYS.ONLINE)}
                </option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            <FieldTitle htmlFor="targetType">{t('wbs.target-type')}</FieldTitle>
            <SelectGroup>
              <select
                id="targetType"
                value={targetType}
                style={{ color: 'var(--grey-900)' }}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'in-radius' || value === 'whole-region') {
                    setTargetType(value);
                  }
                }}
              >
                <option value="in-radius">{t('wbs.in-radius')}</option>
                <option value="whole-region">{t('wbs.whole-region')}</option>
              </select>
              <span className="icon">
                <FontAwesomeIcon icon={faCaretDown} />
              </span>
            </SelectGroup>
          </FormField>

          <FormField>
            <FieldTitle htmlFor="city" $hasError={cityError}>
              {t('wbs.city')}
            </FieldTitle>
            <StyledInput
              id="city"
              ref={inputRef}
              type="text"
              style={{ width: '100%' }}
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                setSelectedCityDisplay('');
                if (e.target.value.trim()) setCityError(false);
              }}
              // Translation Needed
              placeholder={'Enter a location'}
              autoComplete="off"
            />
            {cityError && (
              <FieldError>{t('wbs.this-field-is-required')}</FieldError>
            )}
          </FormField>

          {showRadiusField ? (
            <FormField>
              <FieldTitle htmlFor="radius">{t('wbs.radius-miles')}</FieldTitle>
              <SelectGroup>
                <select
                  id="radius"
                  value={radius}
                  style={{ color: 'var(--grey-900)' }}
                  onChange={(e) => setRadius(e.target.value)}
                >
                  {RADIUS_OPTIONS.map((value) => (
                    <option key={value} value={String(value)}>
                      {value}
                    </option>
                  ))}
                </select>
                <span className="icon">
                  <FontAwesomeIcon icon={faCaretDown} />
                </span>
              </SelectGroup>
              {radiusError && (
                <FieldError>{t('wbs.this-field-is-required')}</FieldError>
              )}
            </FormField>
          ) : (
            <FormField>
              <FieldTitle htmlFor="wholeRegionCity">
                {t('wbs.select-your-target-city')}
              </FieldTitle>
              <WholeRegionChoice>
                <WholeRegionText>
                  <WholeRegionSubtitleRow>
                    {!!selectedCityDisplay && (
                      <input
                        id="wholeRegionCity"
                        type="radio"
                        checked
                        readOnly
                      />
                    )}
                    <WholeRegionSubtitle $hasCity={!!selectedCityDisplay}>
                      {selectedCityDisplay || t('wbs.please-enter-a-place')}
                    </WholeRegionSubtitle>
                  </WholeRegionSubtitleRow>
                </WholeRegionText>
              </WholeRegionChoice>
            </FormField>
          )}

          <WBSButton wide type="submit" disabled={isCreating || isUpdating}>
            {isEditing ? t('wbs.update') : t('actions.add')}
          </WBSButton>
        </form>
      </FormContainer>
    </>
  );
}

const RefusalBox = styled.div`
  margin-bottom: 18px;
`;

const RefusalTitle = styled.strong`
  display: block;
  font-family: var(--raleway);
  font-size: 14px;
`;

const RefusalMessage = styled.span`
  font-family: var(--raleway);
  font-size: 13px;
`;

const WholeRegionChoice = styled.label`
  align-items: flex-start;
  cursor: default;
  display: flex;
  padding: 0;
  width: 100%;
`;

const WholeRegionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WholeRegionSubtitle = styled.span<{ $hasCity: boolean }>`
  color: ${({ $hasCity }) => ($hasCity ? 'var(--grey-900)' : 'var(--wbs-red)')};
  font-family: var(--raleway);
  font-size: 13px;
  font-style: ${({ $hasCity }) => ($hasCity ? 'normal' : 'italic')};
`;

const WholeRegionSubtitleRow = styled.span`
  align-items: center;
  display: flex;
  gap: 8px;

  input {
    accent-color: var(--wbs-red);
    flex-shrink: 0;
    margin: 0;
  }
`;
